import {
  AreaChartOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  TableOutlined,
} from '@ant-design/icons';
import type { AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';
import type React from 'react';

import ComboChartIcon from '@/assets/icons/ComboChartIcon';
import CrossAnalysisIcon from '@/assets/icons/CrossAnalysisIcon';
import type { IconProps } from '@/assets/icons/types/types';
import WordCloudIcon from '@/assets/icons/WordCloudIcon';
import {
  CHART_CALCULATION,
  CHART_COLOR_SCALE,
  CHART_COMPARE_TERM_TYPE,
  CHART_FIELD,
  CHART_FIELD_ITEM_SELECTION_METHOD,
  CHART_FREQUENCY,
  CHART_GRAPH,
  CHART_GRAPH_DIRECTION,
  CHART_METRIC,
  CHART_METRIC_TYPE,
  CHART_METRIC_VIEW_TYPE,
  CHART_PERIOD_RELATIVE,
  CHART_SHARE_CALCULATION_TYPE,
  CHART_TERM_TYPE,
  CHART_TERM_UNIT,
  CHART_TYPE,
} from '@/entities/chart/model/constants';
import { GreenBaseColor, RedBaseColor } from '@/styles/colors/baseColors';
import { getAlphaColor } from '@/styles/colors/color.utils';
import { FeedbackSentiment, sentimentMap } from '@/types/feedbackConst';
import {
  ACCOUNT_COLUMNS,
  CONTACT_COLUMNS,
  DEMOGRAPHIC_COLUMNS,
  FEEDBACK_COLUMNS,
  PRODUCT_COLUMNS,
  SCORE_COLUMNS,
  SENTIMENT_COLUMNS,
  SOURCE_COLUMNS,
} from '@/types/filterKeyConst';
import { ConstMap } from '@/utils/ConstMap';

// Maps for display labels
export const ChartTypeMap = new ConstMap([
  [CHART_TYPE.SINGLE_AXIS, 'Segmentation'],
  [CHART_TYPE.CROSS_AXIS, 'Cross analysis'],
]);

export const ChartFieldMap = new ConstMap([
  [CHART_FIELD.FEEDBACK_ALL, 'All feedback'],
  [CHART_FIELD.FEEDBACK_SOURCE, 'Source Type'],
  [CHART_FIELD.FEEDBACK_DATA_ORIGIN, 'Data Origin'],
  [CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL, 'Sentiments'],
  [CHART_FIELD.FEEDBACK_SENTIMENT, 'Key sentiment'],
  [CHART_FIELD.FEEDBACK_CATEGORY, 'Category'],
  [CHART_FIELD.FEEDBACK_RATING_SCORE, 'Rating Score'],
  [CHART_FIELD.FEEDBACK_NPS_SCORE, 'NPS Score'],
  [CHART_FIELD.FEEDBACK_CES_SCORE, 'CES Score'],
  [CHART_FIELD.FEEDBACK_CSAT, 'CSAT'],
  [CHART_FIELD.FEEDBACK_AGENT, 'Agent'],
  [CHART_FIELD.FEEDBACK_REMARKS, 'Remarks'],
  [CHART_FIELD.FEEDBACK_TAG, 'Tag'],
  [CHART_FIELD.FEEDBACK_HASHTAG, 'Hashtag'],
  [CHART_FIELD.TAXONOMY_TOPIC, 'Taxonomy'],

  [CHART_FIELD.ACCOUNT_NAME, 'Name'],
  [CHART_FIELD.ACCOUNT_INDUSTRY, 'Industry'],
  [CHART_FIELD.ACCOUNT_COUNTRY, 'Country'],
  [CHART_FIELD.ACCOUNT_RATING, 'Rating'],
  [CHART_FIELD.ACCOUNT_SIZE, 'Size'],
  [CHART_FIELD.ACCOUNT_PLAN, 'Plan'],
  [CHART_FIELD.ACCOUNT_TYPE, 'Type'],
  [CHART_FIELD.ACCOUNT_USECASE, 'Primary Usecase'],
  [CHART_FIELD.ACCOUNT_STAGE, 'Stage'],
  [CHART_FIELD.ACCOUNT_ACTIVE_STATUS, 'Active Status'],
  [CHART_FIELD.ACCOUNT_AGENT, 'Agent'],

  [CHART_FIELD.CONTACT_NAME, 'Name'],
  [CHART_FIELD.CONTACT_PLAN, 'Plan'],
  [CHART_FIELD.CONTACT_STAGE, 'Stage'],
  [CHART_FIELD.CONTACT_TYPE, 'Type'],
  [CHART_FIELD.CONTACT_COUNTRY, 'Country'],
  [CHART_FIELD.CONTACT_AGENT, 'Agent'],

  [CHART_FIELD.PRODUCT_BRAND, 'Brand'],
  [CHART_FIELD.PRODUCT_LINE, 'Line'],
  [CHART_FIELD.PRODUCT_PRODUCT, 'Product'],
  [CHART_FIELD.PRODUCT_PRODUCT_OPTION, 'Product option'],
  [CHART_FIELD.DEMOGRAPHIC_GENDER, 'Gender'],
  [CHART_FIELD.DEMOGRAPHIC_AGE, 'Age'],
  [CHART_FIELD.DEMOGRAPHIC_RACE, 'Race'],
  [CHART_FIELD.DEMOGRAPHIC_LOCATION, 'Location'],
  [CHART_FIELD.FEEDBACK_LANGUAGE, 'Language'],
  [CHART_FIELD.FEEDBACK_AD_PREDICTION, 'Ad prediction'],
  [CHART_FIELD.FEEDBACK_MENTION, 'Mention'],
  [CHART_FIELD.FEEDBACK_KEYWORD, 'Keyword'],
  [CHART_FIELD.QUERY, 'Query'],
]);

export const getChartFieldLabel = (field: CHART_FIELD): string => {
  const label = ChartFieldMap.get(field);
  if (!label)
    throw new Error(
      `[getChartFieldLabel] Field label not found for field: ${field}`,
    );
  return label;
};

export const ChartFieldFilterKeyMap: Record<CHART_FIELD, string> = {
  [CHART_FIELD.FEEDBACK_ALL]: 'all',
  [CHART_FIELD.FEEDBACK_SOURCE]: SOURCE_COLUMNS.SOURCE_TYPE,
  [CHART_FIELD.FEEDBACK_DATA_ORIGIN]: SOURCE_COLUMNS.DATA_ORIGIN,
  [CHART_FIELD.FEEDBACK_SENTIMENT]: SENTIMENT_COLUMNS.KEY_SENTIMENT,
  [CHART_FIELD.FEEDBACK_SENTIMENT_DETAIL]: SENTIMENT_COLUMNS.SENTIMENT_DETAIL,
  [CHART_FIELD.FEEDBACK_CATEGORY]: FEEDBACK_COLUMNS.CATEGORY,
  [CHART_FIELD.FEEDBACK_RATING_SCORE]: SCORE_COLUMNS.RATING_SCORE,
  [CHART_FIELD.FEEDBACK_NPS_SCORE]: SCORE_COLUMNS.NPS_SCORE,
  [CHART_FIELD.FEEDBACK_CES_SCORE]: SCORE_COLUMNS.CES_SCORE,
  [CHART_FIELD.FEEDBACK_CSAT]: SCORE_COLUMNS.CSAT_SCORE,
  [CHART_FIELD.FEEDBACK_AGENT]: FEEDBACK_COLUMNS.AGENT,
  [CHART_FIELD.FEEDBACK_REMARKS]: FEEDBACK_COLUMNS.REMARKS,
  [CHART_FIELD.FEEDBACK_TAG]: FEEDBACK_COLUMNS.TAG,
  [CHART_FIELD.FEEDBACK_HASHTAG]: FEEDBACK_COLUMNS.HASHTAG,
  [CHART_FIELD.TAXONOMY_TOPIC]: FEEDBACK_COLUMNS.TAXONOMY_TOPIC,

  [CHART_FIELD.ACCOUNT_NAME]: ACCOUNT_COLUMNS.NAME_SEARCH,
  [CHART_FIELD.ACCOUNT_ACTIVE_STATUS]: ACCOUNT_COLUMNS.ACTIVE_STATUS,
  [CHART_FIELD.ACCOUNT_AGENT]: ACCOUNT_COLUMNS.AGENT,
  [CHART_FIELD.ACCOUNT_INDUSTRY]: ACCOUNT_COLUMNS.INDUSTRY,
  [CHART_FIELD.ACCOUNT_COUNTRY]: ACCOUNT_COLUMNS.COUNTRY,
  [CHART_FIELD.ACCOUNT_RATING]: ACCOUNT_COLUMNS.RATING,
  [CHART_FIELD.ACCOUNT_SIZE]: ACCOUNT_COLUMNS.SIZE,
  [CHART_FIELD.ACCOUNT_PLAN]: ACCOUNT_COLUMNS.PLAN,
  [CHART_FIELD.ACCOUNT_TYPE]: ACCOUNT_COLUMNS.TYPE,
  [CHART_FIELD.ACCOUNT_USECASE]: ACCOUNT_COLUMNS.PRIMARY_USECASE,
  [CHART_FIELD.ACCOUNT_STAGE]: ACCOUNT_COLUMNS.STAGE,

  [CHART_FIELD.CONTACT_NAME]: CONTACT_COLUMNS.NAME_SEARCH,
  [CHART_FIELD.CONTACT_PLAN]: CONTACT_COLUMNS.PLAN,
  [CHART_FIELD.CONTACT_STAGE]: CONTACT_COLUMNS.STAGE,
  [CHART_FIELD.CONTACT_TYPE]: CONTACT_COLUMNS.TYPE,
  [CHART_FIELD.CONTACT_COUNTRY]: CONTACT_COLUMNS.COUNTRY,
  [CHART_FIELD.CONTACT_AGENT]: CONTACT_COLUMNS.AGENT,

  [CHART_FIELD.PRODUCT_BRAND]: PRODUCT_COLUMNS.BRAND,
  [CHART_FIELD.PRODUCT_LINE]: PRODUCT_COLUMNS.LINE,
  [CHART_FIELD.PRODUCT_PRODUCT]: PRODUCT_COLUMNS.PRODUCT,
  [CHART_FIELD.PRODUCT_PRODUCT_OPTION]: PRODUCT_COLUMNS.PRODUCT_OPTION,

  [CHART_FIELD.DEMOGRAPHIC_GENDER]: DEMOGRAPHIC_COLUMNS.GENDER,
  [CHART_FIELD.DEMOGRAPHIC_AGE]: DEMOGRAPHIC_COLUMNS.AGE,
  [CHART_FIELD.DEMOGRAPHIC_LOCATION]: DEMOGRAPHIC_COLUMNS.LOCATION,
  [CHART_FIELD.DEMOGRAPHIC_RACE]: DEMOGRAPHIC_COLUMNS.RACE,

  [CHART_FIELD.FEEDBACK_LANGUAGE]: FEEDBACK_COLUMNS.LANGUAGE,
  [CHART_FIELD.FEEDBACK_AD_PREDICTION]: FEEDBACK_COLUMNS.AD_PREDICTION,
  [CHART_FIELD.FEEDBACK_MENTION]: FEEDBACK_COLUMNS.MENTION,
  [CHART_FIELD.FEEDBACK_KEYWORD]: FEEDBACK_COLUMNS.KEYWORD,
  [CHART_FIELD.QUERY]: FEEDBACK_COLUMNS.QUERY,
};

export const getChartFieldFilterKey = (field: CHART_FIELD): string => {
  const filterKey = ChartFieldFilterKeyMap[field];
  if (!filterKey) throw new Error(`Filter key not found for field: ${field}`);

  return filterKey;
};

export const ChartGraphMap = new ConstMap([
  [CHART_GRAPH.WORD_CLOUD, 'Wordcloud'],
  [CHART_GRAPH.BAR, 'Bar'],
  [CHART_GRAPH.LINE, 'Line'],
  [CHART_GRAPH.PIE, 'Pie'],
  [CHART_GRAPH.BAR_STACKED, 'Stacked bar'],
  [CHART_GRAPH.AREA, 'Area'],
  [CHART_GRAPH.AREA_STACKED, 'Stacked area'],
  [CHART_GRAPH.TABLE, 'Data table'],
  [CHART_GRAPH.COMBO_CHART, 'Combo'],
]);

export const ChartGraphIconMap = new ConstMap<
  CHART_GRAPH,
  React.FC<IconProps> | React.ForwardRefExoticComponent<AntdIconProps>
>([
  [CHART_GRAPH.WORD_CLOUD, WordCloudIcon],
  [CHART_GRAPH.BAR, BarChartOutlined],
  [CHART_GRAPH.LINE, LineChartOutlined],
  [CHART_GRAPH.PIE, PieChartOutlined],
  [CHART_GRAPH.BAR_STACKED, BarChartOutlined],
  [CHART_GRAPH.AREA, AreaChartOutlined],
  [CHART_GRAPH.AREA_STACKED, AreaChartOutlined],
  [CHART_GRAPH.TABLE, TableOutlined],
  [CHART_GRAPH.COMBO_CHART, ComboChartIcon],
]);

export const ChartMetricMap = new ConstMap([
  [CHART_METRIC.FEEDBACK, 'Feedback count'],
  [CHART_METRIC.UNIQUE_ACCOUNT, 'Unique account count'],
  [CHART_METRIC.UNIQUE_CONTACT, 'Unique contact count'],
  [CHART_METRIC.ACCOUNT_REVENUE_IMPACT, 'Account revenue Impact'],
  [CHART_METRIC.CONTACT_REVENUE_IMPACT, 'Contact revenue Impact'],
  [CHART_METRIC.RATING_SCORE, 'Rating score'],
  [CHART_METRIC.NPS_SCORE, 'NPS score'],
  [CHART_METRIC.CSAT_SCORE, 'CSAT score'],
  [CHART_METRIC.CES_SCORE, 'CES score'],
  [CHART_METRIC.TOTAL_ENGAGEMENT, 'Total engagement'],
  [CHART_METRIC.ENGAGEMENT_RATE, 'Engagement rate'],
  [CHART_METRIC.IMPRESSION, 'Impression'],
]);

export const ChartCalculationMap = new ConstMap([
  [CHART_CALCULATION.COUNT, 'Count'],
  [CHART_CALCULATION.SUM, 'Sum'],
  [CHART_CALCULATION.AVERAGE, 'Average'],
]);

export const ChartTermTypeMap = new ConstMap([
  [CHART_TERM_TYPE.PERIODIC, 'Periodic'],
  [CHART_TERM_TYPE.FIXED, 'Fixed'],
]);

export const ChartWordCloudDatasetMap = new ConstMap([
  [CHART_FIELD.FEEDBACK_KEYWORD, 'Keyword'],
  [CHART_FIELD.FEEDBACK_HASHTAG, 'Hashtag'],
  [CHART_FIELD.FEEDBACK_MENTION, 'Mention'],
]);

export const ChartTermUnitMap = new ConstMap([
  [CHART_TERM_UNIT.DAY, 'Day'],
  [CHART_TERM_UNIT.WEEK, 'Week'],
  [CHART_TERM_UNIT.MONTH, 'Month'],
  [CHART_TERM_UNIT.QUARTER, 'Quarter'],
]);

export const ChartPeriodRelativeMap = new ConstMap([
  [CHART_PERIOD_RELATIVE.INCLUDE, 'Including'],
  [CHART_PERIOD_RELATIVE.EXCLUDE, 'Excluding'],
]);

export const ChartCompareTermTypeMap = new ConstMap([
  [CHART_COMPARE_TERM_TYPE.PREVIOUS, 'Previous period'],
  [CHART_COMPARE_TERM_TYPE.YEAR_BEFORE, 'Prior year'],
  [CHART_COMPARE_TERM_TYPE.FIXED, 'Custom: Choose starting date'],
]);

export const ChartFieldItemSelectionMethodMap = new ConstMap([
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5, 'Top 5'],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_10, 'Top 10'],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_20, 'Top 20'],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_30, 'Top 30'],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_40, 'Top 40'],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50, 'Top 50'],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_100, 'Top 100'],
]);

export const ChartTopNumberMap = new ConstMap([
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_5, 5],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_10, 10],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_20, 20],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_30, 30],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_40, 40],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_50, 50],
  [CHART_FIELD_ITEM_SELECTION_METHOD.TOP_100, 100],
]);

export const ChartFrequencyMap = new ConstMap([
  [CHART_FREQUENCY.DAY, 'Day'],
  [CHART_FREQUENCY.WEEK, 'Week'],
  [CHART_FREQUENCY.MONTH, 'Month'],
]);

export const ChartGraphDirectionMap = new ConstMap([
  [CHART_GRAPH_DIRECTION.VERTICAL, 'Vertical'],
  [CHART_GRAPH_DIRECTION.HORIZONTAL, 'Horizontal'],
]);

export const ChartMetricViewTypeMap = new ConstMap([
  [CHART_METRIC_VIEW_TYPE.COUNT, 'Count'],
  [CHART_METRIC_VIEW_TYPE.PERCENTAGE, 'Percentage'],
]);

export const ChartColorScaleMap = new ConstMap([
  [
    CHART_COLOR_SCALE.WHITE_TO_GREEN,
    [
      getAlphaColor(GreenBaseColor.green500, 0),
      getAlphaColor(GreenBaseColor.green500, 0.25),
      getAlphaColor(GreenBaseColor.green500, 0.5),
      getAlphaColor(GreenBaseColor.green500, 0.75),
      getAlphaColor(GreenBaseColor.green500, 1),
    ],
  ],
  [
    CHART_COLOR_SCALE.GREEN_TO_WHITE,
    [
      getAlphaColor(GreenBaseColor.green500, 1),
      getAlphaColor(GreenBaseColor.green500, 0.75),
      getAlphaColor(GreenBaseColor.green500, 0.5),
      getAlphaColor(GreenBaseColor.green500, 0.25),
      getAlphaColor(GreenBaseColor.green500, 0),
    ],
  ],
  [
    CHART_COLOR_SCALE.WHITE_TO_RED,
    [
      getAlphaColor(RedBaseColor.red600, 0),
      getAlphaColor(RedBaseColor.red600, 0.25),
      getAlphaColor(RedBaseColor.red600, 0.5),
      getAlphaColor(RedBaseColor.red600, 0.75),
      getAlphaColor(RedBaseColor.red600, 1),
    ],
  ],
  [
    CHART_COLOR_SCALE.RED_TO_WHITE,
    [
      getAlphaColor(RedBaseColor.red600, 1),
      getAlphaColor(RedBaseColor.red600, 0.75),
      getAlphaColor(RedBaseColor.red600, 0.5),
      getAlphaColor(RedBaseColor.red600, 0.25),
      getAlphaColor(RedBaseColor.red600, 0),
    ],
  ],
  [
    CHART_COLOR_SCALE.GREEN_TO_RED,
    [
      getAlphaColor(GreenBaseColor.green500, 1),
      getAlphaColor(GreenBaseColor.green500, 0.5),
      getAlphaColor(GreenBaseColor.green500, 0),
      getAlphaColor(RedBaseColor.red600, 0.5),
      getAlphaColor(RedBaseColor.red600, 1),
    ],
  ],
  [
    CHART_COLOR_SCALE.RED_TO_GREEN,
    [
      getAlphaColor(RedBaseColor.red600, 1),
      getAlphaColor(RedBaseColor.red600, 0.5),
      getAlphaColor(RedBaseColor.red600, 0),
      getAlphaColor(GreenBaseColor.green500, 0.5),
      getAlphaColor(GreenBaseColor.green500, 1),
    ],
  ],
]);

export const ChartMetricTypeMap = new ConstMap([
  [CHART_METRIC.FEEDBACK, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.UNIQUE_ACCOUNT, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.UNIQUE_CONTACT, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.ACCOUNT_REVENUE_IMPACT, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.CONTACT_REVENUE_IMPACT, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.RATING_SCORE, CHART_METRIC_TYPE.SCORE],
  [CHART_METRIC.NPS_SCORE, CHART_METRIC_TYPE.SCORE],
  [CHART_METRIC.CSAT_SCORE, CHART_METRIC_TYPE.SCORE],
  [CHART_METRIC.CES_SCORE, CHART_METRIC_TYPE.SCORE],
  [CHART_METRIC.TOTAL_ENGAGEMENT, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.ENGAGEMENT_RATE, CHART_METRIC_TYPE.COUNT],
  [CHART_METRIC.IMPRESSION, CHART_METRIC_TYPE.COUNT],
]);

export const ChartTypeIconDic: Record<CHART_TYPE, React.FC> = {
  [CHART_TYPE.SINGLE_AXIS]: LineChartOutlined,
  [CHART_TYPE.CROSS_AXIS]: CrossAnalysisIcon,
};

// Sentiment related mappings that depend on feedbackConst
export const ChartSentimentFieldItemList = Object.values(FeedbackSentiment);

export const ChartShareCalculationTypeMap = new ConstMap([
  [CHART_SHARE_CALCULATION_TYPE.GRAND_TOTAL, '% of grand total'],
  [CHART_SHARE_CALCULATION_TYPE.ROW, '% of row'],
  [CHART_SHARE_CALCULATION_TYPE.COLUMN, '% of column'],
  [CHART_SHARE_CALCULATION_TYPE.CELL, '% of cell'],
]);

export const ChartSentimentIconMap = new ConstMap([
  [FeedbackSentiment.POSITIVE, sentimentMap[FeedbackSentiment.POSITIVE].icon],
  [FeedbackSentiment.NEGATIVE, sentimentMap[FeedbackSentiment.NEGATIVE].icon],
]);
